import { useState, useEffect, createContext, useContext, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser, addBreadcrumb } from "@/lib/sentry";
import type { User, Session } from "@supabase/supabase-js";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isOwner: boolean;
  accessDenied: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const OWNER_EMAIL = "hello@josiahroche.co";

type AccessResult = "allowed" | "denied" | "unknown";

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

async function checkAccess(email: string | undefined): Promise<AccessResult> {
  if (!email) return "denied";
  if (email === OWNER_EMAIL) return "allowed";

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data, error } = (await withTimeout(
        (supabase.rpc as any)("check_team_access", { _email: email }),
        2500,
        "check_team_access",
      )) as { data: unknown; error: { message: string } | null };
      if (!error) return data === true ? "allowed" : "denied";
      console.warn(`check_team_access RPC failed (attempt ${attempt + 1}):`, error.message);
    } catch (e) {
      console.warn(`check_team_access threw (attempt ${attempt + 1}):`, (e as Error).message);
    }
    if (attempt === 0) await new Promise((r) => setTimeout(r, 500));
  }

  // RPC failed twice — return unknown so we don't sign out a valid user
  console.warn("check_team_access failed after retries; treating as unknown (fail-open, not signing out)");
  return "unknown";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const lastSessionTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const isOnInvitePage = () => window.location.pathname.startsWith("/invite/");

    const handleSession = async (s: Session | null) => {
      // Skip duplicate events with the same token to avoid redundant work,
      // but never drop a *new* session in favour of an older one.
      const token = s?.access_token ?? null;
      if (token && token === lastSessionTokenRef.current) {
        setLoading(false);
        return;
      }
      lastSessionTokenRef.current = token;

      if (!s?.user) {
        setSession(null);
        setUser(null);
        setSentryUser(null);
        setLoading(false);
        return;
      }

      // On the invite page we accept the session as-is; activation happens there.
      if (isOnInvitePage()) {
        setSession(s);
        setUser(s.user);
        setSentryUser({ id: s.user.id, email: s.user.email });
        setAccessDenied(false);
        setLoading(false);
        return;
      }

      const result = await checkAccess(s.user.email);

      if (result === "denied") {
        addBreadcrumb({
          category: "auth",
          level: "warning",
          message: "access denied",
          data: { email: s.user.email },
        });
        setAccessDenied(true);
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setSentryUser(null);
        lastSessionTokenRef.current = null;
        setLoading(false);
        return;
      }

      // allowed OR unknown (RPC failure): keep the session. RLS still protects data.
      setSession(s);
      setUser(s.user);
      setSentryUser({ id: s.user.id, email: s.user.email });
      setAccessDenied(false);
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        addBreadcrumb({
          category: "auth",
          message: `auth: ${event}`,
          data: { hasSession: !!session },
        });
        handleSession(session);
      }
    );

    // Hard-bound the initial session lookup. If Supabase is slow to respond
    // (cold start, flaky network), don't trap the whole app on the loading
    // skeleton — flip to "no session" so the login page can render. The
    // onAuthStateChange listener above will catch up if a session arrives later.
    withTimeout(supabase.auth.getSession(), 3000, "getSession")
      .then(({ data: { session } }) => handleSession(session))
      .catch((e) => {
        console.warn("[useAuth] getSession failed/timed out:", (e as Error).message);
        handleSession(null);
      });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    setAccessDenied(false);
    // Use the owned Supabase project's Google OAuth flow directly.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/login`,
        queryParams: { prompt: "select_account" },
      },
    });
    if (error) {
      console.error("[useAuth] Google sign-in failed:", error.message);
      throw error;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const isOwner = user?.email === OWNER_EMAIL;

  return (
    <AuthContext.Provider value={{ user, session, loading, isOwner, accessDenied, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
